/**
 * WeaponEvents - Weapon-Related Socket Event Handlers
 *
 * Handles events related to weapon actions:
 * - weaponReloadStarted: Play reload sound for local player
 * - weaponEmpty: Play empty click sound for local player
 */

import { weaponRegistry } from '../../../shared/WeaponRegistry.js';
import { ClientLogger } from '../../logger/ClientLogger.js';

const weaponEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {}, debug: () => {} };
})();
const weaponEventsLogger = ClientLogger.getLogger('combat') || weaponEventsLoggerFallback;

const WeaponEvents = {
  /**
   * Register all weapon-related event handlers
   * @param {Socket} socket - Socket.io socket instance
   * @param {GameState} gameState - Game state container
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.getWeaponSound - Get weapon sound for playback
   */
  register(socket, gameState, callbacks) {
    const { getWeaponSound } = callbacks;

    // Track currently playing reload sound for interruption
    let currentReloadSound = null;
    let reloadSoundTimers = [];  // Track timers for incremental reload sounds
    let activeReloadClones = []; // cloneNode() instances for incremental reloads

    // Weapon reload started - play reload sound
    socket.on('weaponReloadStarted', (data) => {
      weaponEventsLogger.debug('[WeaponEvents] Received weaponReloadStarted:', data);
      if (!data || !data.weaponId) {
        weaponEventsLogger.warn('[WeaponEvents] Missing data or weaponId');
        return;
      }

      const localPlayer = gameState.players.get(gameState.localId);
      if (!localPlayer) {
        weaponEventsLogger.warn('[WeaponEvents] No local player found');
        return;
      }

      // Get the weapon object from the registry
      const weapon = typeof weaponRegistry !== 'undefined' ? weaponRegistry.get(data.weaponId) : null;
      if (!weapon) {
        weaponEventsLogger.warn('[WeaponEvents] Weapon not found in registry:', data.weaponId);
        return;
      }

      // For incremental reload (shotguns), play sounds on client-side timer for perfect timing
      if (weapon.reloadType === 'incremental' && data.roundsNeeded && data.reloadPerRound) {
        weaponEventsLogger.debug('[WeaponEvents] Starting incremental reload sounds:', {
          rounds: data.roundsNeeded,
          interval: data.reloadPerRound
        });
        
        const reloadSound = getWeaponSound(weapon, 'reload');
        if (!reloadSound) {
          weaponEventsLogger.warn('[WeaponEvents] No reload sound found for weapon:', data.weaponId);
          return;
        }

        // Clear any existing timers
        reloadSoundTimers.forEach(timer => clearTimeout(timer));
        reloadSoundTimers = [];

        // Play sound immediately for first round, then on intervals
        for (let i = 0; i < data.roundsNeeded; i++) {
          const timer = setTimeout(() => {
            const sound = reloadSound.cloneNode();  // Clone to play multiple instances
            // cloneNode() does not copy HTMLMediaElement.volume — it defaults to 1.
            sound.volume = reloadSound.volume;
            sound.currentTime = 0;
            activeReloadClones.push(sound);
            sound.play().catch(err => {
              weaponEventsLogger.warn('[WeaponEvents] Failed to play reload sound:', err);
            });
          }, i * data.reloadPerRound);
          
          reloadSoundTimers.push(timer);
        }
        
        return;
      }

      // For full reload (magazines), play the reload sound once
      weaponEventsLogger.debug('[WeaponEvents] Getting reload sound for weapon:', data.weaponId);
      const reloadSound = getWeaponSound(weapon, 'reload');
      if (reloadSound) {
        weaponEventsLogger.debug('[WeaponEvents] Playing reload sound');
        reloadSound.currentTime = 0;
        reloadSound.play().catch(err => {
          weaponEventsLogger.warn('[WeaponEvents] Failed to play reload sound:', err);
        });
        // Track this sound so we can stop it if reload is interrupted
        currentReloadSound = reloadSound;
      } else {
        weaponEventsLogger.warn('[WeaponEvents] No reload sound found for weapon:', data.weaponId);
      }
    });

    const stopReloadSounds = () => {
      // Clear any scheduled reload sound timers
      reloadSoundTimers.forEach(timer => clearTimeout(timer));
      reloadSoundTimers = [];

      // Stop the currently playing reload sound
      if (currentReloadSound) {
        weaponEventsLogger.debug('[WeaponEvents] Stopping reload sound');
        currentReloadSound.pause();
        currentReloadSound.currentTime = 0;
        currentReloadSound = null;
      }

      for (const sound of activeReloadClones) {
        try {
          sound.pause();
          sound.currentTime = 0;
        } catch (err) {
          // Already stopped
        }
      }
      activeReloadClones = [];
    };

    // Weapon reload interrupted - stop reload sound
    socket.on('weaponReloadInterrupted', (data) => {
      weaponEventsLogger.debug('[WeaponEvents] Received weaponReloadInterrupted:', data);
      stopReloadSounds();
    });

    // Game ended - stop all weapon sounds and timers
    socket.on('gameEnd', () => {
      stopReloadSounds();
    });

    // Match start - cut a reload that began during warm-up so it does not
    // carry into the round. Sounds started after this event play normally.
    socket.on('gameStarted', () => {
      stopReloadSounds();
    });

    // Weapon empty - play empty click sound
    socket.on('weaponEmpty', (data) => {
      weaponEventsLogger.debug('[WeaponEvents] Received weaponEmpty:', data);
      if (!data || !data.weaponId) {
        weaponEventsLogger.warn('[WeaponEvents] Missing data or weaponId');
        return;
      }

      const localPlayer = gameState.players.get(gameState.localId);
      if (!localPlayer) {
        weaponEventsLogger.warn('[WeaponEvents] No local player found');
        return;
      }

      // Get the weapon object from the registry
      const weapon = typeof weaponRegistry !== 'undefined' ? weaponRegistry.get(data.weaponId) : null;
      if (!weapon) {
        weaponEventsLogger.warn('[WeaponEvents] Weapon not found in registry:', data.weaponId);
        return;
      }

      // Play empty click sound for the weapon
      weaponEventsLogger.debug('[WeaponEvents] Getting empty sound for weapon:', data.weaponId);
      const emptySound = getWeaponSound(weapon, 'empty');
      if (emptySound) {
        weaponEventsLogger.debug('[WeaponEvents] Playing empty sound');
        emptySound.currentTime = 0;
        emptySound.play().catch(err => {
          weaponEventsLogger.warn('[WeaponEvents] Failed to play empty sound:', err);
        });
      } else {
        weaponEventsLogger.warn('[WeaponEvents] No empty sound found for weapon:', data.weaponId);
      }
    });

    // Weapon stacked - weapon upgrade notification
    socket.on('weaponStacked', (data) => {
      if (!data) return;

      weaponEventsLogger.info('[WeaponEvents] Weapon stacked:', data);

      // Show notification for local player
      if (data.playerId === gameState.localId) {
        const { showNotification } = callbacks;
        if (showNotification) {
          const levelText = data.newStacks === data.maxStacks ? 'MAX' : `Level ${data.newStacks}`;
          showNotification(`${data.weaponName} upgraded to ${levelText}!`, 'success');
        }

        // Play upgrade/stack sound if available
        // TODO: Add weapon upgrade sound
      }
    });

    // Weapon pickup failed - max stacks warning
    socket.on('weaponPickupFailed', (data) => {
      if (!data) return;

      weaponEventsLogger.warn('[WeaponEvents] Weapon pickup failed:', data);

      const { showNotification } = callbacks;
      if (showNotification && data.message) {
        showNotification(data.message, 'warning');
      }
    });

    // Weapon dropped - another player dropped a weapon
    socket.on('weaponDropped', (data) => {
      if (!data) return;

      weaponEventsLogger.info('[WeaponEvents] Weapon dropped:', data);

      // The dropped weapon pickup is handled by pickupSpawned event
      // This event is mainly for logging/UI feedback
    });
  }
};

// Export for use in other modules
// For now, WeaponEvents is a global object loaded via script tag

export { weaponEventsLoggerFallback, weaponEventsLogger, WeaponEvents };
